import { Capacitor } from '@capacitor/core';
import { openDB, IDBPDatabase } from 'idb';

export type LocalEntityType =
  | 'workouts'
  | 'machine_sessions'
  | 'routines'
  | 'calendar_logs'
  | 'statistics'
  | 'fitness_profiles';

export interface LocalEntity<T = any> {
  key: string;
  type: LocalEntityType;
  id: string;
  userId: string;
  sortKey: number;
  value: T;
}

interface LocalDriver {
  initialize(): Promise<void>;
  put(entity: LocalEntity): Promise<void>;
  get<T>(type: LocalEntityType, id: string): Promise<LocalEntity<T> | undefined>;
  delete(type: LocalEntityType, id: string): Promise<void>;
  list<T>(type: LocalEntityType, userId: string): Promise<LocalEntity<T>[]>;
  clearUser(userId: string): Promise<void>;
  all(userId: string): Promise<LocalEntity[]>;
}

const DATABASE_NAME = 'pawerlifting-local';
const DATABASE_VERSION = 1;

class IndexedDbDriver implements LocalDriver {
  private db?: IDBPDatabase;

  async initialize() {
    this.db = await openDB(DATABASE_NAME, DATABASE_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('entities', { keyPath: 'key' });
        store.createIndex('by-type-user', ['type', 'userId']);
        store.createIndex('by-user', 'userId');
      },
    });
  }

  private ready() {
    if (!this.db) throw new Error('Local database has not been initialized');
    return this.db;
  }

  put(entity: LocalEntity) {
    return this.ready().put('entities', entity).then(() => undefined);
  }
  get<T>(type: LocalEntityType, id: string) {
    return this.ready().get('entities', `${type}:${id}`) as Promise<LocalEntity<T> | undefined>;
  }
  delete(type: LocalEntityType, id: string) {
    return this.ready().delete('entities', `${type}:${id}`).then(() => undefined);
  }
  list<T>(type: LocalEntityType, userId: string) {
    return this.ready().getAllFromIndex('entities', 'by-type-user', [type, userId]) as Promise<LocalEntity<T>[]>;
  }
  async clearUser(userId: string) {
    const tx = this.ready().transaction('entities', 'readwrite');
    let cursor = await tx.store.index('by-user').openCursor(userId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  }
  all(userId: string) {
    return this.ready().getAllFromIndex('entities', 'by-user', userId) as Promise<LocalEntity[]>;
  }
}

class SqliteDriver implements LocalDriver {
  private connection: any;

  async initialize() {
    const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
    const sqlite = new SQLiteConnection(CapacitorSQLite);
    const consistency = await sqlite.checkConnectionsConsistency();
    const existing = await sqlite.isConnection(DATABASE_NAME, false);
    this.connection = consistency.result && existing.result
      ? await sqlite.retrieveConnection(DATABASE_NAME, false)
      : await sqlite.createConnection(DATABASE_NAME, false, 'no-encryption', DATABASE_VERSION, false);
    await this.connection.open();
    await this.migrate();
  }

  private async migrate() {
    const versionResult = await this.connection.query('PRAGMA user_version;');
    const version = Number(versionResult.values?.[0]?.user_version || 0);
    if (version < 1) {
      await this.connection.execute(`
        CREATE TABLE IF NOT EXISTS local_entities (
          entity_key TEXT PRIMARY KEY NOT NULL,
          entity_type TEXT NOT NULL,
          entity_id TEXT NOT NULL,
          user_id TEXT NOT NULL,
          sort_key INTEGER NOT NULL,
          json TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_local_entities_type_user
          ON local_entities(entity_type, user_id, sort_key);
        CREATE INDEX IF NOT EXISTS idx_local_entities_user
          ON local_entities(user_id);
        PRAGMA user_version = 1;
      `);
    }
  }

  async put(entity: LocalEntity) {
    await this.connection.run(
      `INSERT OR REPLACE INTO local_entities
       (entity_key, entity_type, entity_id, user_id, sort_key, json)
       VALUES (?, ?, ?, ?, ?, ?);`,
      [entity.key, entity.type, entity.id, entity.userId, entity.sortKey, JSON.stringify(entity.value)]
    );
  }
  async get<T>(type: LocalEntityType, id: string) {
    const result = await this.connection.query(
      'SELECT * FROM local_entities WHERE entity_key = ? LIMIT 1;',
      [`${type}:${id}`]
    );
    return result.values?.[0] ? this.fromRow<T>(result.values[0]) : undefined;
  }
  async delete(type: LocalEntityType, id: string) {
    await this.connection.run('DELETE FROM local_entities WHERE entity_key = ?;', [`${type}:${id}`]);
  }
  async list<T>(type: LocalEntityType, userId: string) {
    const result = await this.connection.query(
      'SELECT * FROM local_entities WHERE entity_type = ? AND user_id = ? ORDER BY sort_key DESC;',
      [type, userId]
    );
    return (result.values || []).map((row: any) => this.fromRow<T>(row));
  }
  async clearUser(userId: string) {
    await this.connection.run('DELETE FROM local_entities WHERE user_id = ?;', [userId]);
  }
  async all(userId: string) {
    const result = await this.connection.query('SELECT * FROM local_entities WHERE user_id = ?;', [userId]);
    return (result.values || []).map((row: any) => this.fromRow(row));
  }
  private fromRow<T>(row: any): LocalEntity<T> {
    return {
      key: row.entity_key,
      type: row.entity_type,
      id: row.entity_id,
      userId: row.user_id,
      sortKey: Number(row.sort_key),
      value: JSON.parse(row.json),
    };
  }
}

let driverPromise: Promise<LocalDriver> | undefined;

async function getDriver(): Promise<LocalDriver> {
  if (!driverPromise) {
    driverPromise = (async () => {
      const driver: LocalDriver = Capacitor.isNativePlatform()
        ? new SqliteDriver()
        : new IndexedDbDriver();
      await driver.initialize();
      return driver;
    })();
  }
  return driverPromise;
}

export const localDatabase = {
  async put<T>(type: LocalEntityType, id: string, userId: string, value: T, sortKey = Date.now()) {
    const driver = await getDriver();
    await driver.put({ key: `${type}:${id}`, type, id, userId, sortKey, value });
  },
  async get<T>(type: LocalEntityType, id: string) {
    return (await getDriver()).get<T>(type, id);
  },
  async delete(type: LocalEntityType, id: string) {
    return (await getDriver()).delete(type, id);
  },
  async list<T>(type: LocalEntityType, userId: string) {
    const rows = await (await getDriver()).list<T>(type, userId);
    return rows.sort((a, b) => b.sortKey - a.sortKey);
  },
  async clearUser(userId: string) {
    return (await getDriver()).clearUser(userId);
  },
  async all(userId: string) {
    return (await getDriver()).all(userId);
  },
};
